// Per-car condition: tire life, fuel, damage. Plug-and-play — the
// integration surface is three function calls (createCondition on
// car spawn, updateCondition each tick before physics, applyImpact-
// Damage from collision routines). Physics never reads
// car.condition directly — instead this module resolves
// car.effectiveSpec from spec + condition each tick, and physics
// reads from effectiveSpec. Keeps physics pure and the feedback
// loop explicit.
//
// Closed-loop effects:
//   tire wear  → grip falls off as tires degrade
//   fuel burn  → mass drops as fuel runs out (faster, looser car)
//   damage     → engine + steering scaled down past hits;
//                inoperable once damage > 0.5 (50 % health)
//
// Determinism: all updates are pure functions of
// (previous condition, derived telemetry, inputs, dt). No
// Date.now / Math.random. Same world + same inputs ⇒ same
// condition trajectory across replays.

import { getCompound } from '../../shared/tire-compounds.js';

// Tire life dropoff scale. tireLife (0..1) decreases per tick by
//   (combinedG / TIRE_WEAR_G_REF) × compound.wearRate × dt × TIRE_WEAR_RATE.
// At combinedG = TIRE_WEAR_G_REF (a hard but routine corner) on a
// medium compound, tires last roughly TIRE_WEAR_BUDGET_S seconds of
// continuous loading — sized so a typical race stint of 20-30 min
// of mixed loading drops tires from fresh to badly worn.
const TIRE_WEAR_G_REF      = 1.5;       // reference combined-G for
                                        // wear (1g lateral + 1g long
                                        // is ~1.4 g combined, common
                                        // late-brake-into-corner load)
const TIRE_WEAR_BUDGET_S   = 1500;      // seconds at G_REF on medium
                                        // to reach tireLife = 0.
const TIRE_WEAR_PER_SEC    = 1 / TIRE_WEAR_BUDGET_S;

// Tire grip falloff with wear. Bald tires don't have ZERO grip —
// they have ~70 % of fresh on a typical race compound. Linear ramp
// from full to TIRE_GRIP_FLOOR over the wear range.
const TIRE_GRIP_FLOOR      = 0.7;

// ---- tire temperature ----
// Ambient track temperature (°C). Hardcoded for now; will move to a
// per-track field (track.weather.airTemp / surfaceTemp) once weather
// modeling lands.
const AMBIENT_C            = 25;
// Tire temp on grid (°C). A few degrees above ambient to model the
// car sitting in pit lane in the sun, not refrigerated. Lap 1 still
// starts well below the optimal window — warmup laps matter.
const INITIAL_TEMP_C       = 30;
// Heat input from cornering / braking / acceleration. Combined G is
// squared because heat is roughly proportional to friction force
// squared (work-rate scales superlinearly with load). Tuned so a
// car holding ~0.85 g sustained equilibrates around 92 °C — middle
// of the medium-compound optimal window. Real-world race laps average
// lower than 0.85 g (long straights drop the average), so steady-state
// from typical AI driving sits in the lower-optimal range; pushing
// for fastest laps creeps tires up into the hot zone.
const K_HEAT_G             = 1.4;      // °C/s per g²
// Heat input from tire slip (sliding friction). Kept small to avoid
// a runaway: hot tires lose grip → slip grows → if K_HEAT_SLIP were
// large, that extra slip would dump enough heat to overheat the tire
// further, lose more grip, slip more, etc. With K_HEAT_SLIP = 4,
// even a sustained 0.2 rad slide adds only 0.8 °C/s on top of
// cornering heat — meaningful penalty for sloppy driving without
// trapping the tire in a permanent overheat.
const K_HEAT_SLIP          = 4;        // °C/s per |rad| of slip
// Newton's-law-of-cooling base rate toward AMBIENT_C. Multiplied by
// (1 + speed/COOL_SPEED_REF) to model forced convection — a tire
// at 100 m/s sheds heat much faster than one parked. K_COOL is the
// at-rest cooling time constant: 1/0.012 ≈ 83 s to drop 63 % of the
// way toward ambient when stationary.
const K_COOL               = 0.012;    // 1/s base cooling rate
const COOL_SPEED_REF       = 40;       // m/s — speed at which cooling
                                       // effectiveness doubles
// Grip floors for off-window tires. The temp window is about
// "where the tire is happiest", not a cliff between gripless and
// gripped — a real cold or overheated tire still grips, just less
// well. Tuned to a 15 % deficit at either extreme so:
//   - warmup laps are visibly slower (~3-4 s on a 60 s lap), but
//     the car still drives, AI doesn't off itself
//   - overheating is a soft penalty plus accelerated wear
//     (TEMP_WEAR_HOT_MAX), not a sudden grip cliff
// Earlier values (0.60 / 0.70) were closer to "you can't drive";
// real cars don't get that bad until the tire is actually broken.
const TEMP_GRIP_FLOOR_COLD = 0.9;
const TEMP_GRIP_FLOOR_HOT  = 0.85;
// Wear acceleration factor at the hot limit. Linear from 1× at the
// upper edge of the optimal window to TEMP_WEAR_HOT_MAX at the hot
// limit. So a tire run consistently 20-30 °C above optimal wears
// out in 1/3 the time of one held in the window.
const TEMP_WEAR_HOT_MAX    = 3.0;

// ---- tire blowout ----
// Once tireLife hits 0 the tire is "running on the rim" — it still
// has carcass for a few seconds before structural failure. After
// TIRE_BLOWOUT_THRESHOLD_S of accumulated zero-life time the tire
// blows. Sticky flag — only a tire change clears it. Sized so a
// player has roughly a third of a lap at pit-limit speed to limp
// into the pits before the tire actually fails.
//
// After the blowout, the car retains very limited control: grip is
// poor but non-zero (so steering inputs still bend the line), and
// engine / steering / brake authority are clipped to a small
// fraction of normal. The driver can still nurse the car toward the
// runoff — they just can't race it. This is what the AI uses to
// peel disabled cars off the racing line.
const TIRE_BLOWOUT_THRESHOLD_S = 8;
const TIRE_BLOWOUT_GRIP        = 0.30;     // ~30 % of nominal grip
                                           // — enough lateral bite for
                                           // a slow drift off-line at
                                           // speed; not enough to race
const BLOWN_INPUT_AUTHORITY    = 0.30;     // engine / steering / brake
                                           // authority retained after a
                                           // blowout

// Damage tuning. The per-impact math is:
//   delta = (impulse / mass) / DAMAGE_IMPULSE_REF / durability
// where impulse is in kg·m/s (= mass × Δv on the contact normal).
// Setting DAMAGE_IMPULSE_REF = 30 m/s means a head-on 30 m/s wall
// stop on durability 1.0 totals the car (delta = 1). A typical
// 5 m/s scrape costs ~0.17 — significant but recoverable.
const DAMAGE_IMPULSE_REF   = 30;        // m/s of Δv that = 100 % damage
                                        // at durability 1.0

// Health cliff. Below this health (= damage above 1 - cutoff),
// the car is inoperable: engine off, no steering authority. Above
// it, performance degrades linearly from 100 % to ENGINE_FLOOR_AT_CLIFF.
const HEALTH_CLIFF         = 0.5;       // <50 % health = totaled
const ENGINE_FLOOR_AT_CLIFF = 0.5;      // perf at the cliff edge
                                        // (50 % damage = 50 % perf;
                                        // then drops to 0 below cliff)

// Initial condition for a freshly built car. Tires fresh + cold,
// tank full, no damage.
export function createCondition(spec) {
  return {
    tireLife:      1.0,                  // 0..1
    tireTemp:      INITIAL_TEMP_C,       // °C
    tireZeroTimeS: 0,                    // accumulator while tireLife == 0;
                                         // triggers blowout past threshold
    blownTires:    false,                // sticky — clears only on tire change
    fuel:          spec.fuelCapacity,    // kg
    damage:        0.0,                  // 0..1 (0 = pristine)
  };
}

// Reset a car's condition in place. Used by spawnCarsOnGrid so a
// "back to grid" doesn't carry consumables forward across resets.
export function resetCondition(condition, spec) {
  condition.tireLife      = 1.0;
  condition.tireTemp      = INITIAL_TEMP_C;
  condition.tireZeroTimeS = 0;
  condition.blownTires    = false;
  condition.fuel          = spec.fuelCapacity;
  condition.damage        = 0.0;
}

// Per-tick update. Called BEFORE physicsStep so the resolved
// effective spec reflects this tick's wear/burn/temp/damage state.
// Reads previous-tick lateralG/longG/slipAngle (latest available)
// and current inputs (this tick's throttle).
export function updateCondition(car, dt) {
  const cond = car.condition;
  if (!cond) return;
  const spec = car.spec;
  const der  = car.derived;
  const ins  = car.lastInputs || { throttle: 0 };
  const compound = getCompound(spec.tireCompound);

  // ---- tire temperature ----
  // Heat from work done by the tire (combinedG² captures cornering
  // + braking + acceleration in one term) and from sliding friction
  // (slipAngle). Cooling is Newton's law toward ambient, scaled by
  // airflow — a parked tire cools slowly, a tire at 50 m/s cools
  // much faster. Net rate can be positive or negative; tires below
  // ambient are unphysical so we floor there.
  const speed     = Math.hypot(car.vel.x, car.vel.y);
  const combinedG = Math.hypot(der.lateralG || 0, der.longG || 0);
  const slip      = Math.abs(der.slipAngle || 0);
  const heatRate  = K_HEAT_G * combinedG * combinedG + K_HEAT_SLIP * slip;
  const coolRate  = K_COOL * (1 + speed / COOL_SPEED_REF) * (cond.tireTemp - AMBIENT_C);
  cond.tireTemp += (heatRate - coolRate) * dt;
  if (cond.tireTemp < AMBIENT_C) cond.tireTemp = AMBIENT_C;

  // ---- tire wear ----
  // Combined G is the load signal; compound.wearRate scales for soft
  // vs hard rubber; hot tires wear faster (tempWearFactor). The
  // tire's friction ellipse doesn't care which direction the load
  // comes from, so combinedG (not just lateral) is the right input.
  const wearStep  = (combinedG / TIRE_WEAR_G_REF)
                  * compound.wearRate
                  * tempWearFactor(cond.tireTemp, compound)
                  * TIRE_WEAR_PER_SEC
                  * dt;
  cond.tireLife = Math.max(0, cond.tireLife - wearStep);

  // ---- tire blowout ----
  // Once tireLife hits 0 the tire's running on the carcass; structural
  // failure follows after a few seconds of further use. The
  // accumulator only counts time when the car is actually moving
  // (otherwise a stopped car sitting on dead tires would blow them
  // for free). Once blown, the flag is sticky — only a tire change
  // clears it.
  if (!cond.blownTires) {
    if (cond.tireLife <= 0 && speed > 1) {
      cond.tireZeroTimeS += dt;
      if (cond.tireZeroTimeS >= TIRE_BLOWOUT_THRESHOLD_S) {
        cond.blownTires = true;
      }
    } else {
      cond.tireZeroTimeS = 0;       // any non-zero life resets the timer
    }
  }

  // ---- fuel burn ----
  // Throttle ∈ [0, 1]; engine load is roughly linear in throttle for
  // our purposes. Brake/coast = 0 burn (idealization; real engines
  // idle, but the pit-stop loop is the consumer that cares about
  // accumulated burn over a stint).
  if (cond.fuel > 0) {
    const burnStep = ins.throttle * spec.fuelBurnRate * dt;
    cond.fuel = Math.max(0, cond.fuel - burnStep);
  }

  // ---- resolve effective spec ----
  // effectiveSpec is allocated once per car (in createCar); we mutate
  // its fields in place so the per-tick cost is just arithmetic.
  resolveEffectiveSpec(car, compound);
}

// Apply impact damage from a collision. Called from wall + car-car
// collision routines. `impulse` is the normal-direction Δp magnitude
// (kg·m/s) — i.e. mass × |Δv on the contact normal|. Independent of
// who was moving / standing still; only the relative closing rate
// matters, which is what impulse encodes.
export function applyImpactDamage(car, impulse) {
  const cond = car.condition;
  if (!cond) return;
  if (cond.damage >= 1) return;          // already totaled
  const spec = car.spec;
  // Δv on the normal — that's the comparable quantity across cars
  // of different masses. A 700 kg car at 5 m/s carries less damage
  // than a 1500 kg car at 5 m/s, but feels the same impulse-per-kg.
  // Using impulse / mass (= Δv) makes durability a clean per-chassis
  // dial regardless of mass.
  const dv = impulse / Math.max(spec.mass, 1);
  if (dv < 0.5) return;                  // micro-contacts (< 1.8 km/h
                                         // closing) don't damage
  const delta = dv / DAMAGE_IMPULSE_REF / Math.max(spec.durability || 1, 0.1);
  cond.damage = Math.min(1, cond.damage + delta);
  // Effective spec gets re-resolved next tick; no need to do it here.
}

// Mutate car.effectiveSpec to reflect current condition. Physics
// reads from effectiveSpec, so this is where the closed loop closes.
//
// Mass:    spec.mass + condition.fuel  (fuel literally weighs the car
//          down; emptying the tank makes the car ~7 % lighter on
//          50 kg tanks)
// Grip:    spec.grip × tireFactor × compound.gripMul
//          tractionGrip likewise. tireFactor lerps from 1.0 (fresh)
//          to TIRE_GRIP_FLOOR (bald) so worn tires never go to zero
//          — they just become noticeably looser.
// Engine + steering: scaled by performance multiplier, which is
//          1.0 at health 100 %, ENGINE_FLOOR_AT_CLIFF at the cliff
//          edge, 0 below. Inoperable cars coast.
function resolveEffectiveSpec(car, compound) {
  const eff   = car.effectiveSpec;
  const spec  = car.spec;
  const cond  = car.condition;

  // Three multipliers stack into the final grip factor:
  //   tireFactor — degrades with wear, floored at TIRE_GRIP_FLOOR
  //   tempFactor — peaks in the optimal window, drops outside
  //   compound.gripMul — soft/medium/hard baseline
  // Stacked multiplicatively because the limits are independent —
  // hot bald tires lose to BOTH heat and wear.
  const tireFactor = TIRE_GRIP_FLOOR + (1 - TIRE_GRIP_FLOOR) * cond.tireLife;
  const tempFactor = tempGripFactor(cond.tireTemp, compound);
  const gripMul    = tireFactor * tempFactor * compound.gripMul;

  // Damage performance multiplier. Linear from 1.0 at no damage to
  // ENGINE_FLOOR_AT_CLIFF at the cliff edge; 0 past the cliff.
  let perfMul;
  const health = 1 - cond.damage;
  if (health < HEALTH_CLIFF) {
    perfMul = 0;
  } else {
    const t = (health - HEALTH_CLIFF) / (1 - HEALTH_CLIFF);   // 0..1
    perfMul = ENGINE_FLOOR_AT_CLIFF + (1 - ENGINE_FLOOR_AT_CLIFF) * t;
  }

  // Out of fuel — engine cuts, but brakes and steering still work so
  // the driver can coast safely to a halt (and not turn into a brick
  // mid-corner). Different from "totaled" (perfMul = 0 below the
  // health cliff), which kills everything because the car is
  // mechanically broken. Stale momentum bleeds off via drag + rolling
  // resistance + any brake input the AI applies for upcoming corners.
  const outOfFuel = cond.fuel <= 0;

  // Blown tires — rim-on-tarmac with shredded rubber. Grip collapses
  // to TIRE_BLOWOUT_GRIP, and engine / steering / brake authority
  // shrink to BLOWN_INPUT_AUTHORITY of normal so the driver retains
  // limited control: enough to coax the car off-line and onto the
  // runoff, not enough to race. Sticky until a pit stop changes tires.
  const blown          = !!cond.blownTires;
  const blownGripMul   = blown ? TIRE_BLOWOUT_GRIP : 1;
  const blownAuth      = blown ? BLOWN_INPUT_AUTHORITY : 1;

  eff.mass             = spec.mass + cond.fuel;
  eff.grip             = spec.grip * gripMul * blownGripMul;
  eff.tractionGrip     = spec.tractionGrip * gripMul * blownGripMul;
  eff.enginePower      = (perfMul > 0 && !outOfFuel) ? spec.enginePower * perfMul * blownAuth : 0;
  eff.brakeForce       = spec.brakeForce * (perfMul > 0 ? 1 : 0) * blownAuth;
                                        // brakes work until totaled
                                        // (broken hydraulics below the
                                        // health cliff). On blown
                                        // tires the brake itself is
                                        // intact but mostly useless —
                                        // grip is what stops you, so
                                        // we simulate that by clipping
                                        // brake authority too.
  eff.maxSteeringAngle = (perfMul > 0) ? spec.maxSteeringAngle * blownAuth : 0;

  // Fields physics reads but condition doesn't influence — copy
  // through so physicsStep gets the full spec shape from one object.
  eff.steeringRate       = spec.steeringRate;
  eff.steerSpeedRef      = spec.steerSpeedRef;
  eff.minSteerMul        = spec.minSteerMul;
  eff.dragCoeff          = spec.dragCoeff;
  eff.rollingResistance  = spec.rollingResistance;
  eff.wheelbase          = spec.wheelbase;
  eff.yawInertia         = spec.yawInertia;
  eff.frontGripBias      = spec.frontGripBias;
  eff.corneringStiffness = spec.corneringStiffness;
  eff.yawDamping         = spec.yawDamping;
  eff.carLength          = spec.carLength;
  eff.carWidth           = spec.carWidth;
}

// Tire grip as a function of temperature. Piecewise linear:
//   below tempColdLimit             → TEMP_GRIP_FLOOR_COLD
//   tempColdLimit  .. tempOptimalLow  → linear ramp up to 1.0
//   tempOptimalLow .. tempOptimalHigh → 1.0 plateau (the sweet spot)
//   tempOptimalHigh .. tempHotLimit   → linear drop to TEMP_GRIP_FLOOR_HOT
//   above tempHotLimit              → TEMP_GRIP_FLOOR_HOT
// Pure function — exported for any future "what's my grip going to
// be?" UI use, kept module-local for now.
export function tempGripFactor(temp, compound) {
  if (temp >= compound.tempOptimalLow && temp <= compound.tempOptimalHigh) {
    return 1.0;
  }
  if (temp < compound.tempOptimalLow) {
    if (temp <= compound.tempColdLimit) return TEMP_GRIP_FLOOR_COLD;
    const span = compound.tempOptimalLow - compound.tempColdLimit;
    const frac = (temp - compound.tempColdLimit) / span;
    return TEMP_GRIP_FLOOR_COLD + (1 - TEMP_GRIP_FLOOR_COLD) * frac;
  }
  // temp > tempOptimalHigh
  if (temp >= compound.tempHotLimit) return TEMP_GRIP_FLOOR_HOT;
  const span = compound.tempHotLimit - compound.tempOptimalHigh;
  const frac = (temp - compound.tempOptimalHigh) / span;
  return 1.0 - (1.0 - TEMP_GRIP_FLOOR_HOT) * frac;
}

// Wear acceleration multiplier from heat. 1× in or below the optimal
// window, ramping linearly to TEMP_WEAR_HOT_MAX at the hot limit and
// staying there beyond. Cold tires don't wear faster — they just
// don't grip — so the cold side returns 1.0.
export function tempWearFactor(temp, compound) {
  if (temp <= compound.tempOptimalHigh) return 1.0;
  if (temp >= compound.tempHotLimit) return TEMP_WEAR_HOT_MAX;
  const span = compound.tempHotLimit - compound.tempOptimalHigh;
  const frac = (temp - compound.tempOptimalHigh) / span;
  return 1.0 + (TEMP_WEAR_HOT_MAX - 1.0) * frac;
}
